import { getLastContractDay } from '@vy/services';
import { CONTRACT_TYPES } from '@vy/types';
import { isNullOrUndefined, Optional, vyDate, withDefault } from '@vy/utils';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { LocalStorageKeys } from '../../../../constants/local-storage-keys';
import { useCreateContractWithPlan } from '../../../../hooks/requests/use-create-contract-with-plan';
import { useVyNavigate } from '../../../../hooks/use-vy-navigate';
import { queryKeys } from '../../../../services/query-key/query-keys';
import { utcTimeFromDayjs } from '../../../../utils/date';
import { parsePlanInput } from '../../../../utils/parse-plan-input';
import { runDebouncedMutation } from '../../../../utils/vy-mutation';
import { userMessage } from '../../../../utils/vy-toasts';
import {
  invalidateContractsQueries,
  invalidatePlansQueries,
  invalidateCustomersQueries,
  invalidateBillingRevenueQueries,
} from '../../../../utils/query-invalidation';
import { PlanInputForm } from '../create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { buildParentAgreementSettings } from '../../utils';
import { useLocation } from 'react-router-dom';

export const useContractSubmitter = () => {
  const location = useLocation();
  const previousUrl = location.state?.from;
  const navigate = useVyNavigate();
  const queryClient = useQueryClient();
  const createContractWithPlanMutation = useCreateContractWithPlan();

  return useCallback(
    async (planInput: PlanInputForm) => {
      const parsedPlanResult = parsePlanInput(planInput, {
        errorContext: 'Update plan validation',
        allowEmptyProducts: withDefault(planInput.credits?.length, 0) > 0,
        withContractRules: true,
      });

      if (!parsedPlanResult.success) {
        return;
      }

      const parsedPlan = parsedPlanResult.data;
      const customerId = notifyIfMissing(planInput.customerId, 'customerId');
      const startDate = notifyIfMissing(planInput.startDate, 'startDate');
      const planStatus = notifyIfMissing(planInput.status, 'status');

      if (isNullOrUndefined(customerId) || isNullOrUndefined(startDate) || isNullOrUndefined(planStatus)) {
        return;
      }

      const isParentAgreement = planInput.contractType === CONTRACT_TYPES.ParentAgreement;
      const parentAgreementSettings = isParentAgreement ? buildParentAgreementSettings(planInput) : undefined;

      if (createContractWithPlanMutation.isLoading) {
        return;
      }

      await runDebouncedMutation(
        `create-contract-with-plan-${customerId}`,
        createContractWithPlanMutation,
        [
          {
            customerId,
            startDate,
            plan: parsedPlan,
            status: planStatus,
            signatureDate: planInput.signatureDate,
            currencyOverride: planInput.currencyOverride ?? undefined,
            endDate: getContractEndDate(startDate, parsedPlan.billingData.duration),
            contractType: planInput.contractType ?? CONTRACT_TYPES.Standard,
            parentAgreementSettings,
            notes: planInput.notes ?? undefined,
            credits: planInput.credits ?? undefined,
          },
        ],
        {
          pending: 'Creating contract...',
          success: 'Contract created',
          error: 'Failed to create contract',
        },
        {
          onSuccess: async ({ contracts }) => {
            const [createdContract] = contracts;
            const successRedirectUrl = buildSuccessRedirectUrl(previousUrl, customerId, createdContract.id);

            await Promise.all([
              queryClient.invalidateQueries(queryKeys.contractPage(createdContract.id)),
              invalidateContractsQueries(queryClient),
              invalidatePlansQueries(queryClient),
              invalidateCustomersQueries(queryClient),
              invalidateBillingRevenueQueries(queryClient),
            ]);

            localStorage.removeItem(LocalStorageKeys.CreatePlan);

            navigate(successRedirectUrl);
          },
        },
      );
    },
    [createContractWithPlanMutation, navigate, queryClient],
  );
};

function getContractEndDate(startDate: Date, duration: number) {
  const lastContractDay = getLastContractDay(vyDate(startDate), duration);

  return lastContractDay ? utcTimeFromDayjs(lastContractDay) : undefined;
}

function notifyIfMissing<FieldType>(value: Optional<FieldType>, name: string): Optional<FieldType> {
  if (isNullOrUndefined(value)) {
    userMessage('error', `Missing ${name} field`);
  }

  return value;
}

function buildSuccessRedirectUrl(previousUrl: string, customerId: string, contractId: string) {
  const isCameFromCustomersPage = previousUrl.includes('customers');

  const pageBase = isCameFromCustomersPage ? `/customers/${customerId}` : '/plans';

  return `${pageBase}/contracts/${contractId}`;
}
